import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import GameCard from '@/components/base/GameCard';
import { games } from '@/mocks/games';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'rpg');

  useEffect(() => {
    setActiveCategory(slug || 'rpg');
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredGames = games.filter((g) => g.categorySlug === activeCategory);

  const pageTitle = currentCategory
    ? `${currentCategory.name}黄油游戏 - 黄油在线玩`
    : '游戏分类 - 黄油在线玩';

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: currentCategory
      ? `黄油在线玩${currentCategory.name}游戏分类，共${currentCategory.count}款${currentCategory.name}黄油游戏免费在线畅玩。${currentCategory.description}`
      : '黄油在线玩游戏分类页面，按类型筛选你喜欢的黄油游戏。',
    url: `https://huangyouzaxianwan.com/category/${activeCategory}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(pageJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '游戏分类', path: '/category/rpg' },
                  { label: currentCategory?.name || '分类' },
                ]}
              />
              <div className="px-4 md:px-6 pb-8">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                  {currentCategory?.name}黄油游戏
                </h1>
                <p className="text-sm text-foreground-500">
                  {currentCategory?.description} — 共{currentCategory?.count}款游戏
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredGames.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
                {filteredGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-gamepad-line text-2xl text-secondary-500"></i>
                </div>
                <p className="text-foreground-500">该分类暂无游戏，敬请期待！</p>
              </div>
            )}

            {filteredGames.length > 0 && (
              <div className="mt-10 mb-6">
                <h2 className="text-lg font-heading text-foreground-900 mb-4">
                  为什么选择{currentCategory?.name}黄油游戏？
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-background-100 rounded-lg">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 mb-2">
                      <i className="ri-shield-check-line text-primary-600 text-sm"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">免费畅玩</h3>
                    <p className="text-xs text-foreground-500">无需下载安装，打开浏览器即可免费畅玩全部游戏。</p>
                  </div>
                  <div className="p-4 bg-background-100 rounded-lg">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 mb-2">
                      <i className="ri-refresh-line text-accent-600 text-sm"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">每日更新</h3>
                    <p className="text-xs text-foreground-500">每日上新游戏，让你始终有新鲜的游戏体验。</p>
                  </div>
                  <div className="p-4 bg-background-100 rounded-lg">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 mb-2">
                      <i className="ri-device-line text-secondary-600 text-sm"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">多端支持</h3>
                    <p className="text-xs text-foreground-500">PC端与手机端均可畅玩，随时随地享受游戏乐趣。</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}